import React, { useMemo, useState } from 'react';
import {
  AdminPageHeader,
  AdminDevRowMeta,
  btnLink,
  btnPrimary,
  filtersRowClass,
  inputClass,
  selectClass,
} from '../adminUi';
import { useGrading } from './GradingContext';
import { GradingDeleteButton, GradingScoreRange } from './GradingShared';
import { confirmAndRunDelete, formatGradingScore, questionScoreRange } from './utils';
import { deleteGradingQuestion } from '../../../../services/gradingService';

export default function GradingQuestionsTab() {
  const { families, roleClusters, factors, questions, answers, openDrawer, reload } = useGrading();
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [search, setSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [roleClusterFilter, setRoleClusterFilter] = useState('');

  const enriched = useMemo(() => {
    return questions.map((q) => {
      const factor = factors.find((f) => f.id === q.diff_factor_id);
      const rc = roleClusters.find((s) => s.id === q.role_cluster_id);
      const family = factor ? families.find((f) => f.id === factor.family_id) : undefined;
      const qAnswers = answers.filter((a) => a.question_id === q.id);
      const range = questionScoreRange(answers, q.id);
      return { q, factor, family, rc, qAnswers, range };
    }).filter(({ q, factor, family, rc, qAnswers }) => {
      const qSearch = search.trim().toLowerCase();
      if (qSearch) {
        const hit =
          q.question_text.toLowerCase().includes(qSearch) ||
          qAnswers.some((a) => a.answer_text.toLowerCase().includes(qSearch));
        if (!hit) return false;
      }
      if (familyFilter && Number(family?.id) !== Number(familyFilter)) return false;
      if (roleClusterFilter && Number(rc?.id) !== Number(roleClusterFilter)) return false;
      return true;
    });
  }, [questions, factors, roleClusters, families, answers, search, familyFilter, roleClusterFilter]);

  const filteredRoleClusters = roleClusters.filter((s) => !familyFilter || Number(s.family_id) === Number(familyFilter));

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Вопросы и ответы"
        subtitle="Scoring-вопросы: diff_factor × role cluster с вариантами ответов и баллами"
        actions={
          <button type="button" className={btnPrimary} onClick={() => openDrawer({ type: 'question', mode: 'create' })}>
            Добавить вопрос
          </button>
        }
      />

      <div className={filtersRowClass}>
        <input className={inputClass} placeholder="Поиск по тексту вопроса или ответа" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className={selectClass} value={familyFilter} onChange={(e) => { setFamilyFilter(e.target.value); setRoleClusterFilter(''); }}>
          <option value="">Все Family</option>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select className={selectClass} value={roleClusterFilter} onChange={(e) => setRoleClusterFilter(e.target.value)}>
          <option value="">Все Role cluster</option>
          {filteredRoleClusters.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="dc-grading-accordion">
        {enriched.length === 0 && <p className="text-sm text-slate-500">Нет вопросов</p>}
        {enriched.map(({ q, factor, family, rc, qAnswers, range }) => {
          const open = expanded.has(q.id);
          return (
            <div key={q.id} className={`dc-grading-question-card ${open ? 'dc-grading-question-card-open' : ''}`}>
              <button type="button" className="dc-grading-question-head" onClick={() => toggle(q.id)}>
                <div className="flex-1 text-left">
                  <p className="font-medium text-slate-900">{q.question_text}</p>
                  <AdminDevRowMeta parts={[
                    `questions.id=${q.id}`,
                    `questions.diff_factor_id=${q.diff_factor_id}`,
                    `questions.role_cluster_id=${q.role_cluster_id}`,
                  ]} />
                  <p className="dc-grading-meta mt-1">
                    {[family?.name, rc?.name, factor?.name].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">{qAnswers.length} ответов</span>
                  <GradingScoreRange min={range.min} max={range.max} />
                  <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-slate-400`} />
                </div>
              </button>
              {open && (
                <div className="dc-grading-question-body">
                  <ol className="dc-grading-answers-list">
                    {qAnswers.map((a, idx) => (
                      <li key={a.id}>
                        <span className="dc-grading-answer-num">{idx + 1}.</span>
                        <span className="flex-1">{a.answer_text}</span>
                        <AdminDevRowMeta parts={[`answers.id=${a.id}`, `answers.answer_score=${a.answer_score}`]} />
                        <span className="dc-grading-answer-score">{formatGradingScore(a.answer_score)} б.</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-3 flex gap-3">
                    <button type="button" className={btnLink} onClick={() => openDrawer({ type: 'question', mode: 'edit', id: q.id })}>
                      Редактировать
                    </button>
                    <GradingDeleteButton
                      onClick={() => confirmAndRunDelete(
                        q.question_text.slice(0, 60),
                        'Вопрос и все ответы будут удалены.',
                        () => deleteGradingQuestion(q.id),
                        reload,
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
